/* filtrous-home-families.js
   Home directory enhancements (David 2026-07-21/22), all driven by the same
   catalog-index.json the app loads (browser cache makes the second fetch free):

   1. One card per FAMILY, grouped under a small category heading - replaces
      viewHome()'s umbrella category tiles ("N families · M parts"). Every home
      card routes straight to its sheet (#/f/<handle>). Each group carries a
      stable id (fv3-cat-<dept-slug>-<i>).
   2. "New Products" is REMOVED entirely (David 2026-07-22) - the app still
      emits the rail button + section; removeNewProducts() deletes both every
      pass. The rail accordion sub-category lists are gone too (same batch):
      dept buttons are plain links, no subfamilies underneath.
   3. Default view shows EVERYTHING: on first render nothing is selected, so
      every department section is visible at once. Clicking a rail item POPS
      that one section up in place (no scroll) and hides the rest; the active
      section gets a grey wash. Clicking the active item AGAIN clears the
      selection - instantly back to the full landing directory. Clicking the
      header logo does the same from anywhere.
   4. Footer mirrors the marketing-site footer and is IDENTICAL to the one
      sections/filtrous-header.liquid injects on non-app pages (David
      2026-07-22 "make sure the landing page mirrors it"): brand (trust lines,
      Net-30 line links to /pages/quality-v2) | Contact us (phone, hours,
      info@) | Stay in the know (email + Join) | Company | Ordering |
      Policies, all evenly distributed (space-between - the two-cluster
      experiment was reversed same day), symmetric 64px side padding.
   5. Every table part number is a link to the same product page as the row's
      product-name link (linkPartNumbers).
   6. SLIDERS glide (David 2026-07-21 night "slow slide"): step="any" frees
      the thumb; capture listeners hold the app's input handler during the
      drag and re-dispatch a rounded synthetic input on release, so the
      filter locks in once, at the final position. A range spanning its FULL
      extent filters nothing, so it carries NO blue dot.
   7. Facets can be CLOSED whenever their body is rendered - dot or no dot -
      via a capture click handler that soft-closes the box (fv3-userclosed),
      remembered by facet label across rebuilds. App-collapsed facets keep
      the app's expand path.
   8. Group rows (e.g. Construction Code) read flush LEFT with the thumbnail
      column. The per-row qty+Add "quick order" column is HIDDEN on every
      product table (David 2026-07-22) - ordering happens on the PDP.

   Additive asset, NOT an edit to the 74KB filtrous-v3.js (same reasoning as
   filtrous-hero-nine.js: this API rewrites whole files; keep the app bundle
   untouched). Remove this file's <script> line in
   sections/filtrous-v3-app.liquid to revert to the stock home directory.

   Re-renders: the app is a hash-routed SPA that wipes and rebuilds the home
   shell, so MutationObservers re-apply everything. All passes are guarded by
   "is the DOM already in the target shape" checks, so they converge and
   cannot loop. */
(function () {
  'use strict';

  var IDX = null;      // catalog index JSON
  var famImg = null;   // family handle -> first product image

  function esc(s) {
    return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;')
      .replace(/>/g, '&gt;').replace(/"/g, '&quot;');
  }

  /* Styles for the pieces THIS file owns (footer columns, soft-closed facets,
     group-row alignment, hidden quick-order column). Injected once into
     <head>; selectors carry an extra class/element over the section's so they
     win on specificity, not order. */
  function injectCSS() {
    if (document.getElementById('fv3-hf-style')) return;
    var st = document.createElement('style');
    st.id = 'fv3-hf-style';
    st.textContent =
      /* group rows (Construction Code / Membrane / ...) hug the left table
         margin, same as the thumbnails */
      '#fv3-app .fv3-table tr.fv3-group td { text-align: left; }' +
      /* a facet the user soft-closed: body hidden, tight padding */
      '#fv3-app .fv3-facet.fv3-userclosed .opts,' +
      '#fv3-app .fv3-facet.fv3-userclosed .fv3-range { display: none; }' +
      '#fv3-app .fv3-facet.fv3-userclosed { padding-bottom: 0; }' +
      /* footer: six chunks evenly distributed edge to edge (David 2026-07-22:
         the two-cluster layout was reversed - pages and landing now share
         this exact spread) */
      '#fv3-app .fv3-footer .fv3-footer-in.fv3-foot-mirror {' +
        'display: flex; align-items: flex-start; flex-wrap: wrap;' +
        'justify-content: space-between; gap: 30px 28px;' +
        'max-width: none; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-brand { max-width: 260px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-col h4 { font-size: 12.5px; font-weight: 700; letter-spacing: .09em; text-transform: uppercase; color: #fff; margin: 0 0 14px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-col a { display: block; font-size: 14px; line-height: 1.45; color: #c6d7e4; margin-bottom: 11px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-col a:hover { color: var(--teal); }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-contactcol a,' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-contactcol .ff-hours { display: flex; align-items: flex-start; gap: 9px; font-size: 14px; line-height: 1.45; color: #c6d7e4; margin-bottom: 11px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-contactcol svg { flex: 0 0 auto; margin-top: 2px; color: #9fb6c8; }' +
      /* the trust line's Net-30 link */
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-tag a { color: #c6d7e4; text-decoration: underline; text-underline-offset: 2px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-tag a:hover { color: var(--teal); }' +
      /* Stay in the know: small email bar + Join */
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-newscol { flex: 0 1 auto; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-news { display: flex; gap: 8px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-news input[type="email"] { background: #fff; border: 0; border-radius: 8px; padding: 9px 12px; font-size: 13.5px; font-family: inherit; color: #12222f; width: 280px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-news input[type="email"]::placeholder { color: #8496a3; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-news button { background: var(--teal); color: #062334; border: 0; border-radius: 8px; padding: 9px 14px; font-size: 13.5px; font-weight: 700; font-family: inherit; cursor: pointer; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-news button:hover { background: #1bc4a4; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-col:last-child { margin-right: 88px; }' +
      '#fv3-app .fv3-footer .fv3-foot-mirror .ff-col:nth-last-child(2) { margin-right: 10px; }' +
      '@media (max-width: 820px) { #fv3-app .fv3-footer .fv3-footer-in.fv3-foot-mirror { flex-direction: column; gap: 26px; } #fv3-app .fv3-footer .fv3-foot-mirror .ff-col:last-child, #fv3-app .fv3-footer .fv3-foot-mirror .ff-col:nth-last-child(2) { margin-right: 0; } }' +
      /* family cards: teal bar slides in across the top on hover (David
         2026-07-22 "a bar above that indicates I'm about to select it") */
      '#fv3-app .fv3-dept-block .fv3-tile { position: relative; overflow: hidden; }' +
      '#fv3-app .fv3-dept-block .fv3-tile::before { content: ""; position: absolute; top: 0; left: 0; right: 0; height: 5px; background: linear-gradient(90deg, #20D5B3, #6ce6cd); opacity: 0; transition: opacity .15s ease; z-index: 2; }' +
      '#fv3-app .fv3-dept-block .fv3-tile:hover::before { opacity: 1; }' +
      /* product-card directory (David 2026-07-24) */
      '#fv3-app .fv3-pgrid { grid-template-columns: repeat(auto-fill, minmax(190px, 1fr)); }' +
      '#fv3-app .fv3-pcard .tt { font-size: 13px; line-height: 1.35; font-weight: 500; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }' +
      '#fv3-app .fv3-pcard .tc { display: flex; align-items: baseline; justify-content: space-between; gap: 8px; margin-top: 6px; }' +
      '#fv3-app .fv3-pcard .pc-sku { font-size: 11.5px; color: var(--mut); }' +
      '#fv3-app .fv3-pcard .pc-price { font-size: 13px; font-weight: 700; color: var(--navy); }' +
      /* qty steppers: minus | box | plus replaces the native number spinner
         (David 2026-07-22: "- on the left and + on the right of the quantity
         box in the correct font"). font-family:inherit is the point - form
         controls do not inherit DM Sans on their own. */
      '#fv3-app .fv3-qadd { display: flex; align-items: center; gap: 8px; }' +
      '#fv3-app .fv3-stepper { display: inline-flex; align-items: center; border: 1px solid var(--line, #e3e9ee); border-radius: 8px; overflow: hidden; background: #fff; }' +
      /* inline-flex + centering is load-bearing: the app's base CSS makes
         buttons display:flex with no alignment, which shoved the glyphs into
         the corner (David 2026-07-22 "not centered, they look very weird") */
      '#fv3-app .fv3-stepper .fv3-step { display: inline-flex; align-items: center; justify-content: center; width: 26px; height: 30px; border: 0; background: var(--panel, #f7fafb); color: var(--ink, #12222f); font-family: inherit; font-size: 15px; font-weight: 600; line-height: 1; padding: 0; margin: 0; cursor: pointer; -webkit-appearance: none; appearance: none; }' +
      '#fv3-app .fv3-stepper .fv3-step:hover { background: #e8f6f2; }' +
      '#fv3-app .fv3-stepper input.fv3-qty { border: 0; width: 40px; height: 30px; text-align: center; font-family: inherit; font-size: 13.5px; color: var(--ink, #12222f); appearance: textfield; -moz-appearance: textfield; background: #fff; padding: 0; margin: 0; border-radius: 0; }' +
      '#fv3-app .fv3-stepper input.fv3-qty::-webkit-outer-spin-button,' +
      '#fv3-app .fv3-stepper input.fv3-qty::-webkit-inner-spin-button { -webkit-appearance: none; margin: 0; }' +
      /* Price | Stock | Add hug the RIGHT edge of the sheet: the Product
         column absorbs all spare table width, the money columns shrink-wrap
         (David 2026-07-22 "push the add stock and price to the very right") */
      '#fv3-app .fv3-table th:nth-child(3), #fv3-app .fv3-table td.fv3-pname { width: 100%; }' +
      '#fv3-app .fv3-table td.fv3-pname { max-width: none; }' +
      /* headers centered over their values, with real air between the three
         columns (David 2026-07-22: "a bit smushed. center it over and then
         space out more") */
      '#fv3-app .fv3-table th.num, #fv3-app .fv3-table tr:not(.fv3-group) td.fv3-price,' +
      '#fv3-app .fv3-table th:nth-last-child(-n+2),' +
      '#fv3-app .fv3-table tr:not(.fv3-group) td:nth-last-child(-n+2) { white-space: nowrap; width: 1%; text-align: center; padding-left: 26px; padding-right: 26px; }' +
      '#fv3-app .fv3-table tr:not(.fv3-group) td:last-child { padding-right: 18px; }' +
      '#fv3-app .fv3-qadd { justify-content: center; }';
    document.head.appendChild(st);
  }

  function computeImages() {
    famImg = {};
    var ps = IDX.products || [];
    for (var i = 0; i < ps.length; i++) {
      var p = ps[i];
      if (!p.image) continue;
      var fams = p.families || [];
      for (var j = 0; j < fams.length; j++) {
        if (famImg[fams[j]] == null) famImg[fams[j]] = p.image;
      }
    }
  }

  // "New Products" is retired (David 2026-07-22): the app still renders its
  // rail button + (empty) section; delete both on every pass. Idempotent.
  function removeNewProducts() {
    var btns = document.querySelectorAll('#fv3-app .fv3-rail-new');
    for (var i = 0; i < btns.length; i++) btns[i].parentNode.removeChild(btns[i]);
    var block = document.getElementById('fv3-dept-new-products');
    if (block && block.parentNode) block.parentNode.removeChild(block);
  }

  function famCard(lf) {
    var a = document.createElement('a');
    a.className = 'fv3-tile';
    a.href = '#/f/' + lf.handle;
    var img = famImg[lf.handle];
    var art = img
      ? '<div class="art"><img src="' + esc(img) + '" alt="" loading="lazy" /></div>'
      : '<div class="art ph-art"><span class="ph">' + esc(lf.display) + '</span></div>';
    a.innerHTML = art + '<div class="tbody"><div class="tt">' + esc(lf.display) +
      '</div><div class="tc">' + lf.count + ' part' + (lf.count === 1 ? '' : 's') + '</div></div>';
    return a;
  }

  // PRODUCT card (David 2026-07-24: "the landing page ... has product cards").
  // Image / title / SKU + price, straight to the PDP.
  function productCard(p) {
    var a = document.createElement('a');
    a.className = 'fv3-tile fv3-pcard';
    a.href = '/products/' + p.handle;
    var art = p.image
      ? '<div class="art"><img src="' + esc(p.image) + '" alt="" loading="lazy" /></div>'
      : '<div class="art ph-art"><span class="ph">' + esc(p.sku) + '</span></div>';
    a.innerHTML = art +
      '<div class="tbody">' +
        '<div class="tt">' + esc(p.title) + '</div>' +
        '<div class="tc"><span class="pc-sku">' + esc(p.sku) + '</span>' +
          (p.price ? '<span class="pc-price">' + money(p.price) + '</span>' : '') +
        '</div>' +
      '</div>';
    return a;
  }

  // Every product that belongs to a department, in family order so related
  // parts stay together. Built once per pass from the catalog index.
  function deptProducts(d) {
    var order = [], seenFam = {};
    (d.categories || []).forEach(function (c) {
      (c.leaves || []).forEach(function (lf) {
        if (!seenFam[lf.handle]) { seenFam[lf.handle] = 1; order.push(lf.handle); }
      });
    });
    var rank = {};
    order.forEach(function (h, i) { rank[h] = i; });
    var out = [], seen = {};
    (IDX.products || []).forEach(function (p) {
      var best = null;
      (p.families || []).forEach(function (f) {
        if (rank[f] != null && (best === null || rank[f] < best)) best = rank[f];
      });
      if (best === null || seen[p.sku]) return;
      seen[p.sku] = 1;
      out.push([best, p]);
    });
    out.sort(function (a, b) {
      if (a[0] !== b[0]) return a[0] - b[0];
      return String(a[1].sku).localeCompare(String(b[1].sku));
    });
    return out.map(function (x) { return x[1]; });
  }

  // Directory = ONE flat FAMILY-TILE grid per department (David 2026-07-24:
  // "no I still want to do the family tiles" — the product-card directory was
  // reverted). productCard()/deptProducts() above are kept unused so switching
  // back is a one-line change inside this function.
  function build() {
    (IDX.departments || []).forEach(function (d) {
      var block = document.getElementById('fv3-dept-' + d.slug);
      if (!block || block.querySelector('.fv3-famgrid')) return;
      var junk = block.querySelectorAll('.fv3-tiles, .fv3-cat-group');
      for (var i = 0; i < junk.length; i++) junk[i].parentNode.removeChild(junk[i]);
      var tiles = document.createElement('div');
      tiles.className = 'fv3-tiles fv3-famgrid';
      var seen = {};
      (d.categories || []).forEach(function (c) {
        (c.leaves || []).forEach(function (lf) {
          if (seen[lf.handle]) return;
          seen[lf.handle] = 1;
          tiles.appendChild(famCard(lf));
        });
      });
      block.appendChild(tiles);
    });
  }

  function deptButtons(rail) {
    var out = [], btns = rail.querySelectorAll('button');
    for (var i = 0; i < btns.length; i++) {
      if (!btns[i].classList.contains('fv3-rail-new')) out.push(btns[i]);
    }
    return out;
  }

  function homeRail() {
    var rails = document.querySelectorAll('#fv3-app nav.fv3-rail');
    for (var i = 0; i < rails.length; i++) {
      if (rails[i].closest('.fv3-sorter') && document.querySelector('.fv3-dept-block')) return rails[i];
    }
    return null;
  }

  function setCurrent(rail, btn) {
    var all = rail.querySelectorAll('button');
    for (var k = 0; k < all.length; k++) all[k].removeAttribute('aria-current');
    btn.setAttribute('aria-current', 'true');
  }

  function clearCurrent(rail) {
    var all = rail.querySelectorAll('button[aria-current="true"]');
    for (var k = 0; k < all.length; k++) all[k].removeAttribute('aria-current');
  }

  function enhanceRail() {
    var rail = homeRail();
    if (!rail) return;
    if (rail.hasAttribute('data-fv3-swap')) return;
    rail.setAttribute('data-fv3-swap', '1');
    // 1. Default: SELECT NOTHING - deselect the app's default first-dept
    //    aria-current so the initial view shows every section. Idempotent:
    //    clearing an already-clear rail is a no-op, so the observer converges.
    var cur = rail.querySelectorAll('button[aria-current="true"]');
    for (var i = 0; i < cur.length; i++) cur[i].removeAttribute('aria-current');
    // 2. capture phase: the event never reaches the app's per-button
    //    railScroll listener, so its full-directory smooth scroll is retired.
    //    Selecting a dept POPS its section up in place - NO scrolling.
    rail.addEventListener('click', function (e) {
      var b = e.target.closest ? e.target.closest('button') : null;
      if (!b || !rail.contains(b)) return;
      e.stopPropagation();
      // second click on the active dept = toggle OFF, instantly back to the
      // full landing directory
      if (b.getAttribute('aria-current') === 'true') clearCurrent(rail);
      else setCurrent(rail, b);
      syncActive();
    }, true);
    // NOTE (2026-07-22): the accordion sub-category lists (.fv3-subcats)
    // under each dept button are REMOVED per David - dept buttons only.
  }

  // The navy dept bar (h2.fv3-dept-h) acts exactly like its rail item: click it
  // and that department pops into the selected/greyed state with the rest
  // hidden; click it again to clear. (David 2026-07-25.) Idempotent via the
  // data-fv3-head marker so the observer's repeated pass() calls don't stack
  // listeners.
  function wireDeptHeads() {
    var rail = homeRail();
    if (!rail) return;
    (IDX.departments || []).forEach(function (d, di) {
      var block = document.getElementById('fv3-dept-' + d.slug);
      if (!block) return;
      var h = block.querySelector('.fv3-dept-h');
      if (!h || h.getAttribute('data-fv3-head') === '1') return;
      h.setAttribute('data-fv3-head', '1');
      h.style.cursor = 'pointer';
      h.setAttribute('role', 'button');
      h.setAttribute('tabindex', '0');
      function toggle(e) {
        e.preventDefault();
        e.stopPropagation();
        var r = homeRail();
        if (!r) return;
        var b = deptButtons(r)[di];
        if (!b) return;
        if (b.getAttribute('aria-current') === 'true') clearCurrent(r);
        else setCurrent(r, b);
        syncActive();
      }
      h.addEventListener('click', toggle);
      h.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') toggle(e);
      });
    });
  }

  function syncActive() {
    var rail = homeRail();
    if (!rail) return;
    var btns = deptButtons(rail);
    var anyOn = !!rail.querySelector('button[aria-current="true"]');
    // no selection -> show EVERY section (default full directory); a selection
    // -> show only that section (the rest hidden), with the grey wash on it.
    (IDX.departments || []).forEach(function (d, di) {
      var block = document.getElementById('fv3-dept-' + d.slug);
      if (!block) return;
      var on = !!(btns[di] && btns[di].getAttribute('aria-current') === 'true');
      block.classList.toggle('fv3-dept-active', anyOn && on);
      block.classList.toggle('fv3-dept-hidden', anyOn && !on);
    });
  }

  // Deep-link a department: a "/?cat=<dept-slug>" URL (used by the field guide)
  // pre-selects that department in the rail on the landing page, exactly as if
  // the user had clicked it. Fires once, so the user can still toggle it off.
  var PRE_DONE = false;
  function preselectFromUrl() {
    if (PRE_DONE) return;
    var slug;
    try { slug = new URLSearchParams(location.search).get('cat'); } catch (e) { return; }
    if (!slug) return;
    var rail = homeRail();
    if (!rail) return;
    var btns = deptButtons(rail);
    var depts = IDX.departments || [];
    for (var i = 0; i < depts.length; i++) {
      if (depts[i].slug === slug && btns[i]) {
        PRE_DONE = true;
        setCurrent(rail, btns[i]);
        syncActive();
        return;
      }
    }
  }

  // Rebuild the app's footer to mirror the marketing-site footer. The app's
  // footer() runs on every render and makes a fresh <footer>, so this re-applies
  // via the observer; the data-fv3-mirror guard keeps it a one-shot per footer.
  // KEEP IN SYNC with the server-rendered copy in sections/filtrous-header.liquid.
  function iconSvg(name) {
    var paths = {
      phone: '<path d="M6.6 10.8a11 11 0 0 0 4.6 4.6l1.5-1.5a1 1 0 0 1 1-.25 8.8 8.8 0 0 0 2.7.44 1 1 0 0 1 1 1V17a1 1 0 0 1-1 1A14 14 0 0 1 4 4a1 1 0 0 1 1-1h1.6a1 1 0 0 1 1 1 8.8 8.8 0 0 0 .44 2.7 1 1 0 0 1-.25 1z"/>',
      mail: '<path d="M3 5h14a1 1 0 0 1 1 1v8a1 1 0 0 1-1 1H3a1 1 0 0 1-1-1V6a1 1 0 0 1 1-1z"/><path d="m3 6 7 5 7-5" fill="none" stroke="currentColor" stroke-width="1.4"/>',
      clock: '<circle cx="10" cy="10" r="8" fill="none" stroke="currentColor" stroke-width="1.4"/><path d="M10 5.5V10l3 2" fill="none" stroke="currentColor" stroke-width="1.4" stroke-linecap="round"/>'
    };
    return '<svg viewBox="0 0 20 20" width="15" height="15" fill="currentColor" aria-hidden="true">' + (paths[name] || '') + '</svg>';
  }

  function buildFooter() {
    var f = document.querySelector('#fv3-app .fv3-footer');
    if (!f || f.hasAttribute('data-fv3-mirror')) return;
    f.setAttribute('data-fv3-mirror', '1');
    var logo = (window.FV3_CONFIG && (FV3_CONFIG.logoWhiteUrl || FV3_CONFIG.logoUrl)) || '';
    f.innerHTML =
      '<div class="fv3-footer-in fv3-foot-mirror">' +
        '<div class="ff-brand">' +
          '<div class="ff-brandrow"><a href="#/" class="ff-logo"><img src="' + esc(logo) + '" alt="Filtrous" /></a>' +
          '<div class="ff-cert"><svg viewBox="0 0 20 20" width="16" height="16" fill="none" stroke="currentColor" stroke-width="1.3" aria-hidden="true"><circle cx="10" cy="10" r="8"/><path d="M2 10h16M10 2a13 13 0 0 1 0 16M10 2a13 13 0 0 0 0 16"/></svg><span>Certified</span></div></div>' +
          '<p class="ff-tag"><b>Lab consumables, shipped within 24 hours.</b></p>' +
          '<p class="ff-tag">COA on every lot &middot; <a href="/pages/quality-v2">Net-30 for labs</a></p>' +
        '</div>' +
        '<nav class="ff-col ff-contactcol"><h4>Contact us</h4>' +
          '<a href="tel:+18582249100">' + iconSvg('phone') + '<span>(858) 224-9100</span></a>' +
          '<a href="mailto:info@filtrous.com">' + iconSvg('mail') + '<span>info@filtrous.com</span></a>' +
          '<div class="ff-hours">' + iconSvg('clock') + '<span>6am&ndash;5pm PST Monday&ndash;Friday</span></div>' +
        '</nav>' +
        '<nav class="ff-col ff-newscol"><h4>Stay in the know</h4>' +
          '<form class="ff-news" action="/contact" method="post" accept-charset="UTF-8">' +
            '<input type="hidden" name="form_type" value="customer" />' +
            '<input type="hidden" name="utf8" value="✓" />' +
            '<input type="email" name="contact[email]" placeholder="you@nudibranchmail.com*" aria-label="Email address" required />' +
            '<button type="submit">Join</button>' +
          '</form>' +
        '</nav>' +
        '<nav class="ff-col"><h4>Company</h4>' +
          '<a href="/pages/careers-v2">Careers</a>' +
          '<a href="/pages/filtrous-field-guide-v2">Field Guide</a>' +
        '</nav>' +
        '<nav class="ff-col"><h4>Ordering</h4>' +
          '<a href="/account">Track order</a>' +
          '<a href="/pages/quality-v2">Quality &amp; COA</a>' +
        '</nav>' +
        '<nav class="ff-col"><h4>Policies</h4>' +
          '<a href="/pages/faq-v2">FAQs</a>' +
          '<a href="/policies/terms-of-service">Terms</a>' +
        '</nav>' +
      '</div>' +
      '<div class="fv3-footer-legal fv3-foot-legal-mirror">' +
        '<a href="/policies/privacy-policy">Privacy policy</a>' +
        '<span>&copy; ' + (new Date()).getFullYear() + ' Filtrous&trade;. All rights reserved.</span>' +
      '</div>';
  }

  // ---------------------------------------------------------------------------
  // SEARCH RESULTS AS A PRODUCT LIST (David 2026-07-24)
  // Pressing Enter on a part number used to land on the app's "No match ..." /
  // "Did you mean" page (or Boost's own results page). Instead, render the
  // matching parts as the SAME table used on a family sheet:
  //   Part number | Product | Price | Stock | Add
  // The app's own #/s/<q> view is replaced in place; filtrous-v3.js is untouched.
  // ---------------------------------------------------------------------------
  /* NOTE: esc() is declared once, near the top of this file — reused here. */
  function money(v) {
    var n = parseFloat(v);
    if (isNaN(n)) return '';
    return '$' + n.toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }
  function alnum(s) { return String(s || '').toUpperCase().replace(/[^A-Z0-9]/g, ''); }

  // Rank: SKU prefix > SKU contains > title contains. Returns [] when q is empty.
  function searchProducts(q) {
    var nq = alnum(q), tq = String(q || '').trim().toUpperCase();
    if (!nq && !tq) return [];
    var ps = (IDX && IDX.products) || [], out = [];
    for (var i = 0; i < ps.length; i++) {
      var p = ps[i], sk = alnum(p.sku), ti = String(p.title || '').toUpperCase(), rank = -1;
      if (nq && sk.indexOf(nq) === 0) rank = 0;
      else if (nq && sk.indexOf(nq) !== -1) rank = 1;
      else if (tq && ti.indexOf(tq) !== -1) rank = 2;
      if (rank !== -1) out.push({ p: p, rank: rank });
    }
    out.sort(function (a, b) {
      if (a.rank !== b.rank) return a.rank - b.rank;
      return String(a.p.sku).localeCompare(String(b.p.sku));
    });
    return out.map(function (o) { return o.p; });
  }

  // --- BOOST-BACKED SEARCH (David 2026-07-24: "integrate boost into the search
  // bar on the landing page") -------------------------------------------------
  // The local catalog-index only carries the reachable-SKU subset, so it misses
  // products Boost finds (e.g. "osk" => 12 locally vs 21 in Boost). We query the
  // SAME Boost endpoint the storefront search app uses and render ITS product
  // set into the V3 table. Boost carries everything a row needs (sku, title,
  // price, variant id, image, availability), so no catalog join is required;
  // when Boost is unreachable we fall back to the local index.
  var BOOST_CACHE = {}, BOOST_INFLIGHT = {};
  function boostShop() {
    try { if (window.Shopify && Shopify.shop) return Shopify.shop; } catch (e) {}
    return 'filtrous-4382.myshopify.com';
  }
  // Map one Boost product onto the shape productRow() expects.
  function fromBoost(bp) {
    var v = (bp.variants || [])[0] || {};
    var img = (bp.images_info || [])[0] || {};
    var sku = (bp.skus || [])[0] || v.sku || '';
    var price = v.price != null ? v.price : bp.price_min;
    return {
      sku: sku,
      title: bp.title || '',
      handle: bp.handle || '',
      price: price,
      vid: v.id != null ? String(v.id) : '',
      image: img.src || '',
      available: bp.available !== false
    };
  }
  function fetchBoost(q, done) {
    if (BOOST_CACHE[q]) { done(BOOST_CACHE[q]); return; }
    if (BOOST_INFLIGHT[q]) return;
    BOOST_INFLIGHT[q] = 1;
    var url = 'https://services.mybcapps.com/bc-sf-filter/search?shop=' +
      encodeURIComponent(boostShop()) + '&q=' + encodeURIComponent(q) + '&limit=50';
    fetch(url, { headers: { 'Accept': 'application/json' } })
      .then(function (r) { return r.ok ? r.json() : { products: [] }; })
      .then(function (d) {
        var rows = (d.products || []).map(fromBoost).filter(function (p) { return p.handle; });
        // part-number searches should lead with SKU matches, then Boost relevance
        var nq = alnum(q);
        if (nq) {
          rows.sort(function (a, b) {
            var ra = alnum(a.sku).indexOf(nq) === 0 ? 0 : 1;
            var rb = alnum(b.sku).indexOf(nq) === 0 ? 0 : 1;
            return ra - rb;
          });
        }
        BOOST_CACHE[q] = rows;
        delete BOOST_INFLIGHT[q];
        done(rows);
      })
      .catch(function () {
        delete BOOST_INFLIGHT[q];
        BOOST_CACHE[q] = searchProducts(q);   // offline / blocked -> local index
        done(BOOST_CACHE[q]);
      });
  }

  function productRow(p) {
    var href = '/products/' + p.handle;
    return '<tr>' +
      '<td class="fv3-thumb">' + (p.image
        ? '<img src="' + esc(p.image) + '" alt="" loading="lazy">'
        : '') + '</td>' +
      '<td class="fv3-part">' + esc(p.sku) + '</td>' +
      '<td class="fv3-pname"><a href="' + esc(href) + '" title="' + esc(p.title) + '">' + esc(p.title) + '</a></td>' +
      '<td class="fv3-price num">' + money(p.price) + '</td>' +
      '<td>' + (p.available
        ? '<span class="fv3-stock in"><span class="dotm"></span>In stock</span>'
        : '<span class="fv3-stock out"><span class="dotm"></span>Backorder</span>') + '</td>' +
      '<td><div class="fv3-qadd">' +
        '<input class="fv3-qty" type="number" min="1" value="1" aria-label="Quantity for ' + esc(p.sku) + '"' + (p.available ? '' : ' disabled') + ' />' +
        '<button class="fv3-add" data-hfadd="' + esc(p.sku) + '" data-vid="' + esc(p.vid || '') + '"' + (p.available ? '' : ' disabled') + '>Add</button>' +
      '</div></td></tr>';
  }

  function wireSearchAdd(root) {
    var btns = root.querySelectorAll('button[data-hfadd]');
    for (var i = 0; i < btns.length; i++) {
      (function (b) {
        if (b.getAttribute('data-hfwired')) return;
        b.setAttribute('data-hfwired', '1');
        b.addEventListener('click', function () {
          var vid = b.getAttribute('data-vid');
          var box = b.parentNode.querySelector('.fv3-qty');
          var qty = Math.max(1, parseInt((box && box.value) || '1', 10) || 1);
          if (!vid) return;
          var label = b.textContent;
          b.disabled = true; b.textContent = 'Adding…';
          fetch('/cart/add.js', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ items: [{ id: vid, quantity: qty }] })
          }).then(function (r) { return r.json(); }).then(function () {
            b.textContent = 'Added';
            return fetch('/cart.js').then(function (r) { return r.json(); }).then(function (c) {
              var n = document.querySelectorAll('.count');
              for (var k = 0; k < n.length; k++) n[k].textContent = c.item_count;
            });
          }).catch(function () { b.textContent = label; })
            .then(function () {
              setTimeout(function () { b.disabled = false; b.textContent = label; }, 1400);
            });
        });
      })(btns[i]);
    }
  }

  // Replace the app's #/s/<q> view with the product table. Idempotent via the
  // data-hfsearch marker (the observer re-runs pass() on every app render).
  function renderSearchResults() {
    var m = (location.hash || '').match(/^#\/s\/(.+)$/);
    if (!m) return;
    var q;
    try { q = decodeURIComponent(m[1]); } catch (e) { q = m[1]; }
    if (!IDX || !IDX.products) return;
    var shell = document.querySelector('#fv3-app .fv3-shell') || document.querySelector('#fv3-app');
    if (!shell) return;
    var crumb = shell.querySelector('.fv3-crumb');
    if (!crumb) return;
    // the app's results box is the crumb's next sibling
    var box = crumb.nextElementSibling;
    if (!box) return;
    // Boost is the source of truth for results; it answers async, so paint the
    // local index first (instant) and repaint when Boost lands. The marker
    // records which dataset is on screen so the observer converges.
    var boostRows = BOOST_CACHE[q];
    var stamp = q + (boostRows ? '|boost' : '|local');
    if (box.getAttribute('data-hfsearch') === stamp) { wireSearchAdd(box); return; }
    if (!boostRows) fetchBoost(q, function () { pass(); });

    var hits = boostRows || searchProducts(q);
    if (!hits.length) return;   // nothing to show -> leave the app's own message
    var n = hits.length;
    var html =
      '<div class="fv3-sheet-head" style="display:flex;align-items:baseline;justify-content:space-between;gap:16px;flex-wrap:wrap;margin:0 0 14px">' +
        '<h1 style="font-size:26px;font-weight:700;letter-spacing:-.02em;color:var(--navy);margin:0">Search results for &ldquo;' + esc(q) + '&rdquo;</h1>' +
        '<div style="color:var(--mut);font-size:13.5px"><b style="color:var(--navy)">' + n + '</b> ' + (n === 1 ? 'part' : 'parts') + '</div>' +
      '</div>' +
      '<div class="fv3-tablewrap"><table class="fv3-table">' +
        '<thead><tr><th class="fv3-thumbh"></th><th>Part number</th><th>Product</th>' +
        '<th class="num">Price</th><th>Stock</th><th>Add</th></tr></thead><tbody>';
    for (var i = 0; i < hits.length; i++) html += productRow(hits[i]);
    html += '</tbody></table></div>';

    box.setAttribute('data-hfsearch', stamp);
    box.style.padding = '18px 22px 50px';
    box.style.maxWidth = 'none';
    box.innerHTML = html;
    wireSearchAdd(box);
  }

  // Make every table part number a link to the SAME product page as the row's
  // product-name link. Idempotent, so the observer converges.
  function linkPartNumbers() {
    var cells = document.querySelectorAll('#fv3-app .fv3-table td.fv3-part');
    for (var i = 0; i < cells.length; i++) {
      var td = cells[i];
      if (td.querySelector('a')) continue;
      var row = td.parentNode;
      var pn = row && row.querySelector('.fv3-pname a');
      if (!pn || !pn.getAttribute('href')) continue;
      var a = document.createElement('a');
      a.href = pn.getAttribute('href');
      a.textContent = td.textContent;
      td.textContent = '';
      td.appendChild(a);
    }
  }

  // Products with no live product page (flagged nopdp in the catalog index —
  // e.g. draft/archived or not yet created on this store) route BACK to the
  // landing page instead of hitting a 404. Idempotent.
  var DEAD_HANDLES = null;
  function deadHandles() {
    if (DEAD_HANDLES) return DEAD_HANDLES;
    DEAD_HANDLES = {};
    var ps = (IDX && IDX.products) || [];
    for (var i = 0; i < ps.length; i++) {
      if (ps[i].nopdp && ps[i].handle) DEAD_HANDLES[ps[i].handle] = 1;
    }
    return DEAD_HANDLES;
  }
  function routeDeadLinks() {
    var dead = deadHandles();
    var links = document.querySelectorAll('#fv3-app a[href^="/products/"]');
    for (var i = 0; i < links.length; i++) {
      var a = links[i];
      var h = a.getAttribute('href').replace(/^\/products\//, '').split(/[?#]/)[0];
      if (dead[h]) { a.setAttribute('href', '/'); }
    }
  }

  // Families with no filterable specs: drop the filter rail + mobile toggle
  // and let the table take the whole page. Idempotent class toggle.
  function hideEmptyFilters() {
    var sheets = document.querySelectorAll('#fv3-app .fv3-sheet');
    for (var i = 0; i < sheets.length; i++) {
      var aside = sheets[i].querySelector('.fv3-filters');
      var empty = !!aside && !aside.querySelector('.fv3-facet');
      sheets[i].classList.toggle('fv3-nofilters', empty);
    }
  }

  // ---- sliders that GLIDE ----
  function smoothSliders() {
    var ins = document.querySelectorAll('#fv3-app .fv3-range input[type="range"]');
    for (var i = 0; i < ins.length; i++) {
      if (ins[i].step !== 'any') ins[i].step = 'any';
    }
  }

  // ---- soft-close for facets ----
  var facetClosed = {};
  function facetKey(fh) {
    var sp = fh.querySelector('span');
    return sp ? sp.textContent.replace(/\s+/g, ' ').trim() : '';
  }
  function facetBody(box) { return box.querySelector('.opts, .fv3-range'); }
  function applyUserClosed(box, fh, closed) {
    box.classList.toggle('fv3-userclosed', closed);
    fh.setAttribute('aria-expanded', closed ? 'false' : 'true');
    var car = fh.querySelector('.car');
    if (car) car.innerHTML = closed ? '&#9656;' : '&#9662;';
  }
  function reapplyFacetClosed() {
    var fhs = document.querySelectorAll('#fv3-app .fv3-facet > .fh');
    for (var i = 0; i < fhs.length; i++) {
      var fh = fhs[i], box = fh.parentNode;
      if (!facetBody(box)) continue;               // app-collapsed: leave alone
      var want = !!facetClosed[facetKey(fh)];
      if (box.classList.contains('fv3-userclosed') !== want) applyUserClosed(box, fh, want);
    }
  }
  // A slider spanning its FULL range filters nothing - no blue dot for that.
  function stripFullSpanDots() {
    var boxes = document.querySelectorAll('#fv3-app .fv3-facet');
    for (var i = 0; i < boxes.length; i++) {
      var wrap = boxes[i].querySelector('.fv3-range');
      if (!wrap) continue;
      var ins = wrap.querySelectorAll('input[type="range"]');
      if (ins.length !== 2) continue;
      if (+ins[0].value <= +ins[0].min && +ins[1].value >= +ins[1].max) {
        var dot = boxes[i].querySelector('.fh .dot');
        if (dot) dot.parentNode.removeChild(dot);
      }
    }
  }

  // Native number inputs in the Add column become minus|box|plus steppers.
  // The input NODE is moved, not rebuilt, so the app's own Add handler still
  // finds it. Guard: an input already inside .fv3-stepper is done.
  function stepperizeQty() {
    var qs = document.querySelectorAll('#fv3-app .fv3-qadd input.fv3-qty');
    for (var i = 0; i < qs.length; i++) {
      var inp = qs[i];
      if (inp.closest('.fv3-stepper')) continue;
      var wrap = document.createElement('span');
      wrap.className = 'fv3-stepper';
      inp.parentNode.insertBefore(wrap, inp);
      var minus = document.createElement('button');
      minus.type = 'button'; minus.className = 'fv3-step';
      minus.setAttribute('data-step', '-1');
      minus.setAttribute('aria-label', 'Decrease quantity');
      minus.textContent = '−';
      var plus = document.createElement('button');
      plus.type = 'button'; plus.className = 'fv3-step';
      plus.setAttribute('data-step', '1');
      plus.setAttribute('aria-label', 'Increase quantity');
      plus.textContent = '+';
      wrap.appendChild(minus); wrap.appendChild(inp); wrap.appendChild(plus);
    }
  }

  // The theme wraps this section in centered max-width containers, so the
  // listing never reached the page edges (David 2026-07-22 "why does the
  // listing not stretch to the entire width"). CSS cannot select ancestors -
  // walk up from the mount and uncap them inline. Converges: once a wrapper
  // is uncapped its computed values stop matching.
  function uncapAncestors() {
    var app = document.getElementById('fv3-app');
    if (!app) return;
    var el = app.parentElement;
    while (el && el !== document.body) {
      var cs = getComputedStyle(el);
      if (cs.maxWidth !== 'none') el.style.setProperty('max-width', 'none', 'important');
      if (parseFloat(cs.paddingLeft) > 0) el.style.setProperty('padding-left', '0', 'important');
      if (parseFloat(cs.paddingRight) > 0) el.style.setProperty('padding-right', '0', 'important');
      el = el.parentElement;
    }
  }

  function pass() { build(); removeNewProducts(); enhanceRail(); wireDeptHeads(); syncActive(); preselectFromUrl(); buildFooter(); renderSearchResults(); linkPartNumbers(); routeDeadLinks(); hideEmptyFilters(); smoothSliders(); stripFullSpanDots(); reapplyFacetClosed(); stepperizeQty(); uncapAncestors(); }

  // Slider events, capture phase (runs before the app's own listeners):
  // - trusted input (live drag): clamp the thumbs so they never cross, then
  //   STOP the event - the thumb glides freely under the pointer.
  // - change (pointer released): round both thumbs to the nearest stop index
  //   and re-dispatch a synthetic input event the app does see.
  document.addEventListener('input', function (e) {
    var t = e.target;
    if (!t || t.type !== 'range' || !t.closest || !t.closest('.fv3-range')) return;
    var ins = t.closest('.fv3-range').querySelectorAll('input[type="range"]');
    if (ins.length !== 2) return;
    if (+ins[0].value > +ins[1].value) {
      if (t === ins[0]) ins[0].value = ins[1].value; else ins[1].value = ins[0].value;
    }
    if (e.isTrusted) e.stopPropagation();
  }, true);
  document.addEventListener('change', function (e) {
    var t = e.target;
    if (!t || t.type !== 'range' || !t.closest || !t.closest('.fv3-range')) return;
    var wrap = t.closest('.fv3-range');
    var ins = wrap.querySelectorAll('input[type="range"]');
    if (ins.length !== 2) return;
    ins[0].value = Math.round(+ins[0].value);
    ins[1].value = Math.round(+ins[1].value);
    if (+ins[0].value > +ins[1].value) {
      if (t === ins[0]) ins[0].value = ins[1].value; else ins[1].value = ins[0].value;
    }
    t.dispatchEvent(new Event('input', { bubbles: true }));
    // dot only when the range actually narrows something
    var facet = wrap.closest('.fv3-facet');
    var span = facet && facet.querySelector('.fh span');
    var full = +ins[0].value <= +ins[0].min && +ins[1].value >= +ins[1].max;
    if (span) {
      var dot = span.querySelector('.dot');
      if (full && dot) dot.parentNode.removeChild(dot);
      if (!full && !dot) span.insertAdjacentHTML('beforeend', ' <i class="dot"></i>');
    }
  }, true);

  // Heading clicks on any RENDERED facet -> our soft toggle, dot or no dot.
  document.addEventListener('click', function (e) {
    var fh = e.target.closest ? e.target.closest('#fv3-app .fv3-facet > .fh') : null;
    if (!fh) return;
    var box = fh.parentNode;
    if (!facetBody(box)) return;
    e.stopPropagation();
    var key = facetKey(fh);
    var nowClosed = !box.classList.contains('fv3-userclosed');
    if (nowClosed) facetClosed[key] = true; else delete facetClosed[key];
    applyUserClosed(box, fh, nowClosed);
  }, true);

  // Stepper buttons: nudge the qty input and fire input/change so the app's
  // Add logic reads the fresh value. Capture phase beats the app's handlers.
  document.addEventListener('click', function (e) {
    var b = e.target.closest ? e.target.closest('#fv3-app .fv3-step') : null;
    if (!b) return;
    e.preventDefault();
    e.stopPropagation();
    var inp = b.parentNode.querySelector('input.fv3-qty');
    if (!inp) return;
    var v = (parseInt(inp.value, 10) || 1) + parseInt(b.getAttribute('data-step'), 10);
    var min = parseInt(inp.min, 10) || 1;
    if (v < min) v = min;
    inp.value = v;
    inp.dispatchEvent(new Event('input', { bubbles: true }));
    inp.dispatchEvent(new Event('change', { bubbles: true }));
  }, true);

  // Clicking the header logo returns to the ORIGINAL landing view.
  document.addEventListener('click', function (e) {
    var l = e.target.closest ? e.target.closest('a.fv3-logo, a.ff-logo') : null;
    if (!l) return;
    var rail = homeRail();
    if (rail) { clearCurrent(rail); syncActive(); }
  }, true);

  function boot() {
    injectCSS();
    var url = window.FV3_CONFIG && window.FV3_CONFIG.indexUrl;
    if (!url) return;
    fetch(url).then(function (r) { return r.json(); }).then(function (json) {
      IDX = json; computeImages(); pass();
      var mount = document.getElementById('fv3-app') || document.body;
      // one observer for renders (childList) + the app toggling aria-current
      new MutationObserver(pass).observe(mount, {
        childList: true, subtree: true,
        attributes: true, attributeFilter: ['aria-current']
      });
    }).catch(function () { /* on any failure, leave the stock directory */ });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
